/**
 * Compile Gate Table Component
 *
 * Uses DataGrid (TanStack Table + TanStack Virtual) for virtualized rows,
 * sorting, column resize, quick filter, row selection, copy, and CSV export.
 * When groupBy !== 'none', a left GroupRail lists groups; clicking a group filters the grid to that group.
 */

import React, { useMemo, useState, useEffect } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataGrid } from '@/components/data-grid';
import { CompileGateGroupRail, type GroupRailItem } from './CompileGateGroupRail';
import { AcceptRiskModal } from './AcceptRiskModal';
import type { Violation } from '@/hooks/useCompileGateViewModel';

const columnHelper = createColumnHelper<Violation>();

interface CompileGateTableProps {
  violations: Violation[];
  groupedViolations?: Record<string, Violation[]>;
  groupBy?: 'none' | 'rule' | 'severity' | 'wbs';
  sortColumn: string;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string) => void;
  reviewed: Set<string>;
  selected: Set<string>;
  accepted: Set<string>;
  onToggleReviewed: (id: string) => void;
  onToggleSelected: (id: string) => void;
  onAcceptRisk: (id: string, reason: string) => void;
  getViolationId: (v: Violation, index: number) => string;
  compactMode: boolean;
  onBulkMarkReviewed: (ids: string[]) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  allSelected: boolean;
  onExportGroup?: (groupKey: string) => void;
  reviewMode?: boolean;
  setSelected?: (ids: Set<string>) => void;
}

export const CompileGateTable: React.FC<CompileGateTableProps> = ({
  violations,
  groupedViolations,
  groupBy = 'none',
  reviewed,
  selected,
  accepted,
  onToggleReviewed,
  onToggleSelected: _onToggleSelected,
  onAcceptRisk,
  getViolationId,
  compactMode,
  onExportGroup: _onExportGroup,
  reviewMode = true,
  setSelected,
}) => {
  const [acceptRiskModal, setAcceptRiskModal] = React.useState<{ id: string; violation: Violation } | null>(null);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);

  useEffect(() => {
    setSelectedGroupKey(null);
  }, [groupBy]);

  const handleAcceptRisk = (violationId: string, violation: Violation) => {
    setAcceptRiskModal({ id: violationId, violation });
  };

  const handleAcceptRiskConfirm = (reason: string) => {
    if (acceptRiskModal) {
      onAcceptRisk(acceptRiskModal.id, reason);
      setAcceptRiskModal(null);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => el && (el.indeterminate = table.getIsSomeRowsSelected())}
            onChange={table.getToggleAllRowsSelectedHandler()}
            className="rounded"
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={(e) => {
              e.stopPropagation();
              row.toggleSelected();
            }}
            onClick={(e) => e.stopPropagation()}
            className="rounded"
            aria-label="Select row"
          />
        ),
        size: 48,
        minSize: 40,
        maxSize: 56,
      }),
      columnHelper.accessor((r) => r.severity, {
        id: 'severity',
        header: 'Severity',
        cell: ({ getValue }) => {
          const severity = getValue();
          return (
            <Badge variant={(severity?.toLowerCase() as 'destructive' | 'outline' | 'secondary') || 'outline'}>
              {severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : '-'}
            </Badge>
          );
        },
        size: 100,
        minSize: 80,
      }),
      columnHelper.accessor((r) => r.activity_id ?? '-', {
        id: 'activity_id',
        header: 'Activity ID',
        cell: ({ getValue }) => <span className="font-mono">{getValue()}</span>,
        size: 120,
        minSize: 80,
      }),
      columnHelper.accessor((r) => r.activity_name ?? '-', {
        id: 'activity_name',
        header: 'Activity Name',
        cell: ({ getValue }) => <span className="max-w-xs truncate block">{getValue()}</span>,
        size: 200,
        minSize: 120,
      }),
      columnHelper.accessor('rule_name', {
        id: 'rule',
        header: 'Rule',
        size: 160,
        minSize: 100,
      }),
      columnHelper.accessor('message', {
        id: 'message',
        header: 'Message',
        cell: ({ getValue }) => <span className="max-w-md truncate block">{getValue()}</span>,
        size: 280,
        minSize: 150,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const v = row.original;
          const violationId = getViolationId(v, row.index);
          const isReviewed = reviewed.has(violationId);
          const isAccepted = accepted.has(violationId);
          return (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              {reviewMode && (
                <input
                  type="checkbox"
                  checked={isReviewed}
                  onChange={() => onToggleReviewed(violationId)}
                  title="Mark as Reviewed"
                  className="rounded"
                />
              )}
              {v.requires_approval && !isAccepted && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAcceptRisk(violationId, v)}
                  className="h-6 px-2 text-xs"
                >
                  Accept Risk
                </Button>
              )}
              {isAccepted && (
                <Badge variant="outline" className="text-xs">
                  Accepted
                </Badge>
              )}
            </div>
          );
        },
        size: 140,
        minSize: 100,
      }),
    ],
    [reviewed, accepted, onToggleReviewed, getViolationId, reviewMode]
  );

  const onSelectedRowIdsChange = useMemo(() => {
    if (!setSelected) return undefined;
    return (ids: Set<string>) => setSelected(ids);
  }, [setSelected]);

  const tableViolations = useMemo(() => violations, [violations]);

  const groupRailItems: GroupRailItem[] = useMemo(() => {
    if (groupBy === 'none' || !groupedViolations) return [];
    return Object.entries(groupedViolations).map(([groupKey, groupViolations]) => {
      const label =
        groupBy === 'rule'
          ? groupViolations[0]?.rule_name ?? groupKey
          : groupBy === 'severity'
            ? groupViolations[0]?.severity
                ? groupViolations[0].severity.charAt(0).toUpperCase() + groupViolations[0].severity.slice(1)
                : groupKey
            : groupKey;
      return { key: groupKey, label, count: groupViolations.length };
    });
  }, [groupBy, groupedViolations]);

  const gridData = useMemo(() => {
    if (groupBy === 'none' || !groupedViolations) return tableViolations;
    if (selectedGroupKey == null) return tableViolations;
    return groupedViolations[selectedGroupKey] ?? [];
  }, [groupBy, groupedViolations, selectedGroupKey, tableViolations]);

  const showGroupRail = groupBy !== 'none' && groupRailItems.length > 0;

  return (
    <>
      <div className="flex w-full border border-nb-border-subtle rounded-lg overflow-hidden min-h-[300px]">
        {showGroupRail && (
          <CompileGateGroupRail
            groups={groupRailItems}
            selectedKey={selectedGroupKey}
            onSelect={setSelectedGroupKey}
            compactMode={compactMode}
          />
        )}
        <div className={showGroupRail ? 'flex-1 min-w-0 min-h-0 flex flex-col' : 'w-full flex flex-col min-h-0'}>
          <DataGrid<Violation>
            data={gridData}
          columns={columns}
          getRowId={getViolationId}
          density={compactMode ? 'compact' : 'default'}
          enableQuickFilter={true}
          quickFilterPlaceholder="Search violations..."
          enableRowSelection={true}
          selectedRowIds={selected}
          onSelectedRowIdsChange={onSelectedRowIdsChange}
          enableColumnResize={true}
          enableColumnPinning={true}
          enableSorting={true}
          enableCopy={true}
          enableCsvExport={true}
            emptyMessage={selectedGroupKey != null ? 'No violations in this group' : 'No violations match your filters'}
          />
        </div>
      </div>
      {acceptRiskModal && (
        <AcceptRiskModal
          violation={acceptRiskModal.violation}
          onConfirm={handleAcceptRiskConfirm}
          onCancel={() => setAcceptRiskModal(null)}
        />
      )}
    </>
  );
};
